import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Flower2 } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { toast } from "sonner";

const empty = () => ({
  name: "", description: "", image_url: "",
  price_from: "", icon: "", order: 0, active: true,
});

export default function Services() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty());
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/services").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        order: Number(form.order) || 0,
        price_from: form.price_from === "" || form.price_from == null ? null : Number(form.price_from),
      };
      if (editing) await api.put(`/services/${editing}`, payload);
      else await api.post("/services", payload);
      toast.success(editing ? "Servicio actualizado" : "Servicio creado");
      setForm(empty()); setEditing(null); setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const onEdit = (s) => {
    setForm({
      name: s.name || "", description: s.description || "",
      image_url: s.image_url || "", price_from: s.price_from ?? "",
      icon: s.icon || "", order: s.order || 0, active: s.active !== false,
    });
    setEditing(s.id); setShowForm(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm("¿Eliminar servicio?")) return;
    try { await api.delete(`/services/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid="services-page">
      <PageHeader
        title="Servicios"
        subtitle="Servicios que se muestran en la landing pública (carrusel)."
        testid="services-header"
        action={
          <button data-testid="services-create-button"
            onClick={() => { setForm(empty()); setEditing(null); setShowForm(true); }}
            className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nuevo servicio
          </button>
        }
      />

      {showForm && (
        <div data-testid="service-form" className="card-soft p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-xl font-bold">{editing ? "Editar" : "Nuevo"} servicio</h3>
            <button onClick={() => setShowForm(false)} className="text-[#8A9A92] hover:text-[#2A3F35]"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nombre *">
              <input data-testid="service-name" required className="input-soft" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Precio desde (USD)">
              <input data-testid="service-price" type="number" step="0.01" className="input-soft"
                value={form.price_from} onChange={(e) => setForm({ ...form, price_from: e.target.value })} />
            </Field>
            <div className="md:col-span-2">
              <Field label="Descripción">
                <textarea data-testid="service-description" rows={3} className="input-soft" value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label="URL de imagen (opcional)">
                <input data-testid="service-image" className="input-soft" placeholder="https://..."
                  value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
              </Field>
            </div>
            <Field label="Orden">
              <input type="number" className="input-soft" value={form.order}
                onChange={(e) => setForm({ ...form, order: e.target.value })} />
            </Field>
            <label className="flex items-center gap-2 mt-7">
              <input type="checkbox" checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              <span className="text-sm">Activo (visible en la landing)</span>
            </label>
            <div className="md:col-span-2 flex justify-end gap-3">
              <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
              <button data-testid="service-save-button" className="btn-primary" type="submit">Guardar</button>
            </div>
          </form>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState title="Sin servicios" hint="Crea los servicios que ofrece la floristería." testid="services-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="services-grid">
          {items.map((s) => (
            <div key={s.id} className="card-soft p-5 flex gap-4">
              <div className="w-20 h-20 rounded-xl bg-[#F3F2ED] grid place-items-center overflow-hidden shrink-0">
                {s.image_url ? <img src={s.image_url} alt="" className="w-full h-full object-cover" /> : <Flower2 className="w-8 h-8 text-[#8A9A92]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-heading text-base font-bold truncate">{s.name}</p>
                    {s.price_from != null && s.price_from !== "" && (
                      <p className="text-xs text-[#5C6B64]">Desde $ {Number(s.price_from).toFixed(2)}</p>
                    )}
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full ${s.active ? "bg-[#E4EFE6] text-[#507A59]" : "bg-[#F3F2ED] text-[#8A9A92]"}`}>
                    {s.active ? "Activo" : "Oculto"}
                  </span>
                </div>
                {s.description && <p className="text-xs text-[#5C6B64] mt-2 line-clamp-2">{s.description}</p>}
                <div className="mt-3 flex gap-1">
                  <button onClick={() => onEdit(s)} className="p-2 rounded hover:bg-[#F3F2ED]" data-testid={`service-edit-${s.id}`}><Pencil className="w-4 h-4 text-[#5C6B64]" /></button>
                  <button onClick={() => onDelete(s.id)} className="p-2 rounded hover:bg-[#F3DADA]" data-testid={`service-delete-${s.id}`}><Trash2 className="w-4 h-4 text-[#A44A4A]" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
